import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DollarSign, TrendingUp, Target, ChevronDown, Calendar, Clock, Sparkles, Zap } from "lucide-react";

const UnifiedRevenueDashboard = ({ goal, current, status, progressPercentage, goalEndDate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const remaining = goal - current;
  const dataPoints = [20, 35, 28, 45, 38, 55, 48, 62, 58, 70];
  const maxValue = Math.max(...dataPoints);

  // Calculate days remaining
  const calculateDaysRemaining = () => {
    if (!goalEndDate) return null;
    const endDate = new Date(goalEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = calculateDaysRemaining();
  
  // Format the goal end date for display
  const formatGoalDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Get countdown color based on urgency
  const getCountdownColor = () => {
    if (daysRemaining === null) return "text-gray-400";
    if (daysRemaining < 0) return "text-red-400";
    if (daysRemaining <= 30) return "text-red-400";
    if (daysRemaining <= 90) return "text-yellow-400";
    return "text-neon-cyan";
  };

  // Status colors
  const getStatusStyle = () => {
    if (status === "On Pace") return {
      bg: "bg-neon-cyan/10",
      border: "border-neon-cyan/30",
      text: "text-neon-cyan",
      glow: "shadow-neon-cyan"
    };
    if (status === "Needs Acceleration") return {
      bg: "bg-red-500/10",
      border: "border-red-500/30",
      text: "text-red-400",
      glow: "shadow-[0_0_20px_rgba(255,46,46,0.3)]"
    };
    return {
      bg: "bg-neon-green/10",
      border: "border-neon-green/30",
      text: "text-neon-green",
      glow: "shadow-[0_0_20px_rgba(0,255,148,0.3)]"
    };
  };

  const statusStyle = getStatusStyle();

  return (
    <motion.div 
      className="glass-card card-3d cursor-pointer overflow-hidden relative"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      onClick={() => setIsExpanded(!isExpanded)}
      data-testid="unified-revenue-dashboard"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      layout
    >
      {/* Animated background gradient */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-neon-cyan/10 via-transparent to-neon-pink/5" />
      </div>

      {/* Holographic border effect */}
      <div className="absolute inset-0 rounded-xl opacity-50 pointer-events-none holo-border" />

      <div className="p-8 relative z-10">
        {/* Collapsed Header - Always Visible */}
        <div className={`flex items-center justify-between transition-all duration-500 ${isExpanded ? 'mb-8' : 'mb-0'}`}>
          <div className="flex items-center gap-6">
            {/* Revenue Goal Display */}
            <div className="flex flex-col">
              <div className="flex items-center gap-3">
                {/* Floating animated icon */}
                <motion.div 
                  className="float-element"
                  animate={{ 
                    y: [0, -8, 0],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ 
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
              <div className="p-3 rounded-xl bg-gradient-to-br from-neon-cyan/20 to-neon-cyan/5 border border-neon-cyan/20">
                    <DollarSign className="h-8 w-8 text-neon-cyan" />
                  </div>
                </motion.div>
                
                {/* Goal Amount */}
                <div>
                  <span className="text-5xl text-neon-cyan tracking-tight data-value">
                    ${(goal / 1000000).toFixed(1)}M
                  </span>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Revenue Goal</p>
                </div>
              </div>
              
              {/* Goal End Date & Countdown */}
              {goalEndDate && (
                <motion.div 
                  className="flex items-center gap-3 mt-3 ml-14"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                    <Calendar className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-300">Target: {formatGoalDate(goalEndDate)}</span>
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                    daysRemaining <= 30 ? 'bg-red-500/10 border border-red-500/20' : 'bg-neon-cyan/10 border border-neon-cyan/20'
                  }`}>
                    <Clock className={`h-3.5 w-3.5 ${getCountdownColor()}`} />
                    <span className={`text-xs font-semibold ${getCountdownColor()}`} data-testid="days-remaining">
                      {daysRemaining !== null ? (
                        daysRemaining < 0 
                          ? `${Math.abs(daysRemaining)} days overdue`
                          : daysRemaining === 0 
                            ? "Due today!"
                            : `${daysRemaining} days left`
                      ) : "No deadline set"}
                    </span>
                  </div>
                </motion.div>
              )}
            </div>
            
            {/* Progress Percentage */}
            <div className="flex items-center gap-4">
              <div className="h-12 w-px bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Progress</p>
                <p className="text-3xl text-neon-cyan data-value">{Math.round(progressPercentage)}%</p>
              </div>
            </div>
          </div>
          
          {/* Status Badge & Expand Icon */}
          <div className="flex items-center gap-4">
            {/* Status Badge */}
            <motion.div 
              className={`inline-flex items-center gap-2 px-4 py-2 ${statusStyle.bg} border ${statusStyle.border} rounded-full`}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <div className={`h-2 w-2 rounded-full ${status === "On Pace" ? "bg-neon-cyan" : status === "Needs Acceleration" ? "bg-red-400" : "bg-neon-green"}`} />
              <span className={`text-sm font-semibold ${statusStyle.text}`}>
                {status}
              </span>
            </motion.div>
            
            {/* Expand Icon */}
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            >
              <ChevronDown className="h-5 w-5 text-gray-400" />
            </motion.div>
          </div>
        </div>

        {/* Expanded Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-6">
                {/* Left: Progress Gauge */}
                <div className="flex flex-col items-center justify-center">
                  <div className="relative" style={{ width: "200px", height: "100px" }}>
                    <svg className="w-full h-full" viewBox="0 0 200 100">
                      {/* Background arc with glow */}
                      <path
                        d="M 20 100 A 80 80 0 0 1 180 100"
                        fill="none"
                        stroke="rgba(255,255,255,0.05)"
                        strokeWidth="16"
                        strokeLinecap="round"
                      />
                      {/* Progress arc with neon glow */}
                      <path
                        d="M 20 100 A 80 80 0 0 1 180 100"
                        fill="none"
                        stroke="url(#gaugeGradientNeon)"
                        strokeWidth="16"
                        strokeLinecap="round"
                        strokeDasharray={`${(Math.min(100, progressPercentage) / 100) * 251.2} 251.2`}
                        filter="url(#glow)"
                      />
                      <defs>
                        <linearGradient id="gaugeGradientNeon" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#00F0FF" />
                          <stop offset="100%" stopColor="#FF0099" />
                        </linearGradient>
                        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                          <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                          <feMerge>
                            <feMergeNode in="coloredBlur"/>
                            <feMergeNode in="SourceGraphic"/>
                          </feMerge>
                        </filter>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center mt-6">
                        <p className="text-4xl text-neon-cyan data-value">
                          {Math.round(progressPercentage)}%
                        </p>
                        <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Progress</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Center: Revenue Breakdown Cards */}
                <div className="space-y-4">
                  {/* Current Revenue Card */}
                  <motion.div 
                    className="glass-card-light p-4"
                    whileHover={{ scale: 1.02, y: -2 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Current Revenue</span>
                      <motion.div 
                        className="p-1.5 rounded-lg bg-neon-cyan/10"
                        animate={{ rotate: [0, 10, -10, 0] }}
                        transition={{ duration: 3, repeat: Infinity }}
                      >
                        <TrendingUp className="h-4 w-4 text-neon-cyan" />
                      </motion.div>
                    </div>
                    <p className="text-2xl font-bold text-white data-value">${(current / 1000).toFixed(0)}K</p>
                    <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-neon-cyan to-neon-cyan/50"
                        initial={{ width: 0 }}
                        animate={{ width: `${progressPercentage}%` }}
                        transition={{ duration: 1, delay: 0.2 }}
                      />
                    </div>
                  </motion.div>
                  
                  {/* Remaining Card */}
                  <motion.div 
                    className="glass-card-light p-4"
                    whileHover={{ scale: 1.02, y: -2 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Remaining</span>
                      <motion.div 
                        className="p-1.5 rounded-lg bg-neon-pink/10"
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <Target className="h-4 w-4 text-neon-pink" />
                      </motion.div>
                    </div>
                    <p className="text-2xl text-neon-pink data-value">${(remaining / 1000).toFixed(0)}K</p>
                  </motion.div>
                </div>

                {/* Right: Performance Chart */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Performance Trend</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Sparkles className="h-4 w-4 text-neon-green" />
                        <p className="text-sm font-bold text-neon-green">+24% Growth</p>
                      </div>
                    </div>
                    <motion.div 
                      className="p-2 rounded-lg bg-white/5"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                      <Zap className="h-4 w-4 text-neon-yellow" />
                    </motion.div>
                  </div>
                  
                  <div className="relative h-32 glass-card-light rounded-lg p-4">
                    <svg className="w-full h-full" viewBox="0 0 300 120" preserveAspectRatio="none">
                      {/* Gradient Definition */}
                      <defs>
                        <linearGradient id="chartGradientNeon" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.4" />
                          <stop offset="100%" stopColor="#00F0FF" stopOpacity="0" />
                        </linearGradient>
                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#00F0FF" />
                          <stop offset="100%" stopColor="#FF0099" />
                        </linearGradient>
                      </defs>

                      {/* Area fill */}
                      <motion.path
                        d={`M 0 ${120 - (dataPoints[0] / maxValue) * 100} ${dataPoints
                          .map((val, idx) => `L ${(idx / (dataPoints.length - 1)) * 300} ${120 - (val / maxValue) * 100}`)
                          .join(' ')} L 300 120 L 0 120 Z`}
                        fill="url(#chartGradientNeon)"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1 }}
                      />

                      {/* Line with glow */}
                      <motion.path
                        d={`M 0 ${120 - (dataPoints[0] / maxValue) * 100} ${dataPoints
                          .map((val, idx) => `L ${(idx / (dataPoints.length - 1)) * 300} ${120 - (val / maxValue) * 100}`)
                          .join(' ')}`}
                        fill="none"
                        stroke="url(#lineGradient)"
                        strokeWidth="3"
                        strokeLinecap="round"
                        filter="url(#glow)"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 1.5, ease: "easeInOut" }}
                      />

                      {/* Data points */}
                      {dataPoints.map((val, idx) => (
                        <motion.circle
                          key={idx}
                          cx={(idx / (dataPoints.length - 1)) * 300}
                          cy={120 - (val / maxValue) * 100}
                          r="4"
                          fill="#050505"
                          stroke="#00F0FF"
                          strokeWidth="2"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.1 * idx, duration: 0.3 }}
                        />
                      ))}
                    </svg>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default UnifiedRevenueDashboard;
